import React, { useEffect, useRef, useState } from "react";
import { gql, useSubscription } from "@apollo/client";
import { Alert, Button, Snackbar } from "@mui/material";
import {
  REALTIME_CONNECTION_STATES,
  notifyNotificationInboxChanged,
  setRealtimeConnectionState,
} from "../utils/notificationRecovery";

export const NOTIFICATION_INBOX_CHANGED_SUBSCRIPTION = gql`
  subscription OnNotificationInboxChanged {
    notificationCreated {
      id
      read
      type
    }
  }
`;

export function buildNotificationFeedback(type) {
  switch (type) {
    case "PRAYER_REACTION":
      return "Someone prayed for your request.";
    case "COMMENT_ADDED":
    case "COMMENT_REPLY":
      return "Someone responded to your prayer.";
    case "FRIEND_REQUEST":
      return "You received a friend request.";
    case "FRIEND_REQUEST_ACCEPTED":
      return "Your friend request was accepted.";
    case "GROUP_INVITE":
      return "You were invited to a Circle.";
    case "GROUP_JOIN_REQUEST":
      return "A new Circle membership request needs review.";
    case "GROUP_MEMBERSHIP_REMOVED":
      return "Your Circle membership changed.";
    case "GROUP_JOIN_APPROVED":
      return "Your Circle request was approved.";
    case "GROUP_JOIN_DECLINED":
      return "Your Circle request was updated.";
    case "CHURCH_ACCESS_REQUEST":
      return "A new church membership request needs review.";
    case "CHURCH_ACCESS_APPROVED":
      return "Your church membership request was approved.";
    case "CHURCH_ACCESS_DECLINED":
    case "CHURCH_ACCESS_REJECTED":
      return "Your church membership request was updated.";
    case "CHURCH_MEMBERSHIP_ACTIVE":
      return "Your church membership is active.";
    case "CHURCH_MEMBERSHIP_SUSPENDED":
      return "Your church membership was suspended.";
    case "CHURCH_MEMBERSHIP_ENDED":
      return "Your church membership ended.";
    case "CHURCH_WEBSITE_REQUEST_SUBMITTED":
      return "A custom website request needs review.";
    case "CHURCH_WEBSITE_REQUEST_UPDATED":
      return "A custom website request was updated.";
    case "CHURCH_WEBSITE_REQUEST_RECEIVED":
      return "Your custom website request was received.";
    case "CHURCH_WEBSITE_REQUEST_STATUS":
      return "Your custom website request status changed.";
    case "ANNOUNCEMENT":
      return "Your church shared a new announcement.";
    case "CAMPAIGN_MESSAGE":
      return "You have a new church message.";
    default:
      return "You have a new notification.";
  }
}

export default function NotificationInboxSync({ active, onOpenInbox }) {
  const [feedback, setFeedback] = useState(null);
  const lastShownId = useRef(null);
  const { data, error } = useSubscription(
    NOTIFICATION_INBOX_CHANGED_SUBSCRIPTION,
    { skip: !active }
  );

  useEffect(() => {
    const notification = data?.notificationCreated;
    if (!notification?.id) return;
    notifyNotificationInboxChanged();
    if (notification.id === lastShownId.current) return;
    lastShownId.current = notification.id;
    setFeedback(buildNotificationFeedback(notification.type));
  }, [data]);

  useEffect(() => {
    if (error) setRealtimeConnectionState(REALTIME_CONNECTION_STATES.DISCONNECTED);
  }, [error]);

  useEffect(() => {
    if (!active) {
      lastShownId.current = null;
      setFeedback(null);
    }
  }, [active]);

  const closeFeedback = () => setFeedback(null);

  return (
    <Snackbar
      open={Boolean(feedback)}
      autoHideDuration={5000}
      onClose={closeFeedback}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity="info"
        variant="filled"
        role="status"
        aria-live="polite"
        onClose={closeFeedback}
        action={onOpenInbox ? (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeFeedback();
              onOpenInbox();
            }}
            sx={{ minHeight: 44 }}
          >
            View
          </Button>
        ) : undefined}
      >
        {feedback}
      </Alert>
    </Snackbar>
  );
}
